import React from 'react';

import Grid from '@mui/material/Grid2';

import { FeatureSource } from "@models";
import { 
  CharacterItem,
  Item,
  Weapon,
  Armor,
  Ammunition,
  Container,
  PseudoItem,
  Character,
  CharacterFeatureSource,
  CharacterArmor,
  CharacterCustomItem,
  CharacterWeapon,
  WEAPON_KEYWORDS,
  Campaign
} from "@dndModels";
import { RARITY_NUM_TO_STR_MAP } from "@dndModels/Constants";

import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import ViewItem from "@dndComponents/display/ViewItem";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import DisplayObjects from "@dndComponents/display/DisplayObjects";

interface Props {
  item: Item | PseudoItem | CharacterItem;
  character: Character | Campaign;
  log_it?: boolean;
}

const char_util = DandDCharacterUtilities.getInstance();

export default function ItemDetails({
  item,
  character,
  log_it = false
}: Props) {

  const renderCharItem = (char_item: CharacterItem) => {
    const return_us: any[] = [];
    if (char_item instanceof CharacterCustomItem) {
      return_us.push(
        <Grid key="notes">
          <DisplayStringWithReplacements display_string={ char_item._notes } />
        </Grid>
      );
    } else {
      if (char_item instanceof CharacterArmor && char_item.armor_type) {
        let ac = char_item.armor_class;
        if (character instanceof Character) {
          if (char_item.armor_type.dex_bonus_max === null) { // Light = null
            ac += Math.max(character.current_ability_scores.DEX, 0);
          } else if (char_item.armor_type.dex_bonus_max === true) { // Medium = true
            ac += Math.min(2, Math.max(0, character.current_ability_scores.DEX));
          } // Heavy = false
          
          return_us.push(
            <Grid key={`ac`}>
              <span style={{ fontWeight: "bold" }}>Base Armor Class:&nbsp;</span> 
              { ac }
            </Grid>
          );
        } else {
          let ac_str = `${ac}`;
          if (char_item.armor_type.dex_bonus_max === null) { // Light = null
            ac_str += " + DEX";
          } else if (char_item.armor_type.dex_bonus_max === true) { // Medium = true
            ac_str += " + DEX (Max 2)"
          } // Heavy = false
          
          return_us.push(
            <Grid key={`ac`}>
              <span style={{ fontWeight: "bold" }}>Base Armor Class:&nbsp;</span> 
              { ac_str }
            </Grid>
          );
        }
      }
      if ((char_item.root_name === "Potion Of Healing" || 
        char_item.root_name === "Potion of Healing") && char_item.potion_level > 0) {
        const char_options = character.character_options;
        const level = char_item.potion_level;
        let roll_string = `${level}d${char_options.leveled_potion_dice_size}`;
        if (char_options.leveled_potion_bonus_multiplier > 0) {
          roll_string += ` + ${char_options.leveled_potion_bonus_multiplier * level}`;
        } else if (char_options.leveled_potion_bonus_multiplier < 0) {
          roll_string += ` ${char_options.leveled_potion_bonus_multiplier * level}`;
        }
        return_us.push(
          <Grid key={`${char_item.true_id}_desc`}>
            { `You Regain ${roll_string} Hit Points When You Drink This Potion.` }
          </Grid>
        );
      // } else if (char_item.magic_item) {
      //   return_us.push(
      //     <Grid item key="magic_item">
      //       { renderBaseItem(char_item.magic_item, char_item.rarity, false) }
      //     </Grid>
      //   );
      } else if (char_item instanceof CharacterWeapon) {
        return_us.push(
          <Grid key="item">
            { renderCharacterWeapon(char_item, char_item.rarity, char_item.char_magic_item_keywords.length === 0) }
          </Grid>
        );
      } else if (char_item.base_item) {
        return_us.push(
          <Grid key="item">
            { renderBaseItem(char_item.base_item, char_item.rarity, char_item.char_magic_item_keywords.length === 0) }
          </Grid>
        );
      }
      char_item.char_magic_item_keywords.forEach(cmik => {
        return_us.push(
          <Grid key={cmik.true_id}>
            { renderCharacterFeatureSource(cmik) }
          </Grid>
        );
      });
      if (char_item.customizations.notes) {
        const notes = `${char_item.customizations.notes}`;
        return_us.push(
          <Grid key="notes_spacer">&nbsp;</Grid>
        );
        return_us.push(
          <Grid key="notes">
            <DisplayStringWithReplacements display_string={ notes } />
          </Grid>
        );
      }
    }
    return (
      <Grid container spacing={0} direction="column">
        { return_us }
      </Grid>
    );
  }

  const renderPseudoItem = (pseudo_item: PseudoItem) => {
    const return_us: any[] = [];
    const base_item = pseudo_item.base_item;
    return_us.push(
      <Grid key={`item`}>
        <ViewItem item={pseudo_item} />
      </Grid>
    );
    return_us.push(
      <Grid key={`${pseudo_item.id}_desc`}>
        <DisplayStringWithReplacements display_string={ pseudo_item.description } />
      </Grid>
    );
    if (base_item) {
      return_us.push(renderFeatureSource(base_item));
    }
    for (const mik of pseudo_item.magic_item_keywords) {
      return_us.push(renderFeatureSource(mik));
    }
    return (
      <Grid container spacing={0} direction="column">
        { return_us }
      </Grid>
    );
  }

  const renderBaseItem = (base_item: Item, rarity_num: number, show_cost: boolean) => {
    const return_us: any[] = [];
    let rarity = "Common";
    switch (rarity_num) {
      case 1: rarity = "Uncommon"; break;
      case 2: rarity = "Rare"; break;
      case 3: rarity = "Very-Rare"; break;
      case 4: rarity = "Legendary"; break;
      case 5: rarity = "Artifact"; break;
    }
    if (base_item.magical) {
      // const magic_item = base_item as IMagicItem;
      const attunement: any = null;
      // if (magic_item.attunement) {
      //   if (magic_item.limitations.length === 0) {
      //     attunement = <span>(requires attunement)</span>;
      //   } else {
      //     attunement = ( 
      //       <span>
      //         (requires attunement by a&nbsp;
      //           { magic_item.limitations.map((limit, key) => {
      //             return (
      //               <span key={key}>{ key > 0 && ", " }<DisplayObjects type={limit.type} ids={limit.ids} /></span>
      //             );
      //           })}
      //         )
      //       </span>
      //     );
      //   }
      // }
      const base_item_name = "";
      // if (base_item.magical) {
      //   const magic_item = base_item as IMagicItem;
      //   if (magic_item.base_item) {
      //     base_item_name = ` (${magic_item.base_item.name})`;
      //   }
      // }
      return_us.push(
        <Grid key={`${base_item._id}_type`}>
          { ["Other"].includes(base_item.item_type) ? "Wondrous Item" : base_item.item_type }{ base_item_name },&nbsp;{ rarity }&nbsp;{ attunement }
        </Grid>
      );
    } else {
      return_us.push(
        <Grid key={`${base_item._id}_type`}>
          { base_item.item_type }, { rarity }
        </Grid>
      );
    }
    
    if (base_item.weight > 0) {
      return_us.push(
        <Grid key={`${base_item._id}_weight`}>
          <span style={{ fontWeight: "bold" }}>Weight:&nbsp;</span> 
          { base_item.weight } lb.
        </Grid>
      );
    }
    if (show_cost && base_item.cost > 0) {
      return_us.push(
        <Grid key={`${base_item._id}_cost`}>
          <span style={{ fontWeight: "bold" }}>Cost:&nbsp;</span> 
          { base_item.cost } gp
        </Grid>
      );
    }
    if (base_item instanceof Weapon) {
      const weapon_keyword_names = WEAPON_KEYWORDS.filter(o => 
        base_item.weapon_keyword_ids.includes(o._id) &&
        o.display_in_equipment
      ).map(o => o.name);
      if (weapon_keyword_names.includes("Range") || weapon_keyword_names.includes("Ranged")) {
        return_us.push(
          <Grid key={`${base_item._id}_attack_type`}>
            <span style={{ fontWeight: "bold" }}>Attack Type:&nbsp;</span>
            Ranged
          </Grid>
        );
        return_us.push(
          <Grid key={`${base_item._id}_attack_range`}>
            <span style={{ fontWeight: "bold" }}>Range:&nbsp;</span> 
            { base_item.range } ft./{ base_item.range2 } ft.
          </Grid>
        );
        const ranged_attacks = base_item.attacks.Ranged?.rolls??[]; // as RollPlus[];
        if (ranged_attacks && ranged_attacks.length > 0) {
          return_us.push(
            <Grid key={`${base_item._id}_ranged_damage`}>
              <span style={{ fontWeight: "bold" }}>Ranged Damage:&nbsp;</span> 
              { ranged_attacks.map((attack, key) => {
                return (<span key={key}>{ key > 0 && ", " }{ attack.as_string } { attack.type !== "Typeless" && attack.type }</span>);
              })}
            </Grid>
          );
        }
      } else {
        return_us.push(
          <Grid key={`${base_item._id}_attack_type`}>
            <span style={{ fontWeight: "bold" }}>Attack Type:&nbsp;</span> 
            Melee
          </Grid>
        );
        return_us.push(
          <Grid key={`${base_item._id}_attack_reach`}>
            <span style={{ fontWeight: "bold" }}>Reach:&nbsp;</span> 
            { base_item.reach } ft.
          </Grid>
        );
        const melee_attacks = base_item.attacks.Melee?.rolls??[]; // as RollPlus[];
        if (melee_attacks && melee_attacks.length > 0) {
          return_us.push(
            <Grid key={`${base_item._id}_melee_damage`}>
              <span style={{ fontWeight: "bold" }}>Melee Damage:&nbsp;</span> 
              { melee_attacks.map((attack, key) => {
                return (<span key={key}>{ key > 0 && ", " }{ attack.as_string } { attack.type !== "Typeless" && attack.type }</span>);
              })}
            </Grid>
          );
        }
        if (weapon_keyword_names.includes("Versatile")) {
          const versatile_attacks = base_item.attacks.Versatile?.rolls??[]; // as RollPlus[];
          if (versatile_attacks && versatile_attacks.length > 0) {
            return_us.push(
              <Grid key={`${base_item._id}_versatile_damage`}>
                <span style={{ fontWeight: "bold" }}>Versatile Damage:&nbsp;</span> 
                { versatile_attacks.map((attack, key) => {
                  return (<span key={key}>{ key > 0 && ", " }{ attack.as_string } { attack.type !== "Typeless" && attack.type }</span>);
                })}
              </Grid>
            );
          }
        }
        if (weapon_keyword_names.includes("Thrown")) {
          return_us.push(
            <Grid key={`${base_item._id}_attack_range`}>
              <span style={{ fontWeight: "bold" }}>Range:&nbsp;</span> 
              { base_item.range } ft./{ base_item.range2 } ft.
            </Grid>
          );
          const thrown_attacks = base_item.attacks.Thrown?.rolls??[]; // as RollPlus[];
          if (thrown_attacks && thrown_attacks.length > 0) {
            return_us.push(
              <Grid key={`${base_item._id}_thrown_damage`}>
                <span style={{ fontWeight: "bold" }}>Thrown Damage:&nbsp;</span> 
                { thrown_attacks.map((attack, key) => {
                  return (<span key={key}>{ key > 0 && ", " }{ attack.as_string } { attack.type !== "Typeless" && attack.type }</span>);
                })}
              </Grid>
            );
          }
        }
        if (base_item.weapon_mastery_property_id !== "") {
          return_us.push(
            <Grid key={`${base_item._id}_weapon_mastery`}>
              <span style={{ fontWeight: "bold" }}>Weapon Mastery:&nbsp;</span> <DisplayObjects type="weapon_mastery_property" ids={[base_item.weapon_mastery_property_id]} />
            </Grid>
          );
        }
      }
      return_us.push(
        <Grid key={`${base_item._id}_properties`}>
          <span style={{ fontWeight: "bold" }}>Properties:&nbsp;</span> 
          { weapon_keyword_names.join(", ") }
        </Grid>
      );
    } else if (base_item instanceof Armor && base_item.armor_type) {
      let ac = base_item.base_armor_class;
      let detail = "";
      if (base_item.armor_type.name === "Shield") {
        detail += `${ac}`;
      } else if (character instanceof Character) {
        if (base_item.armor_type.dex_bonus_max === null) { // Light = null
          ac += Math.max(character.current_ability_scores.DEX, 0);
        } else if (base_item.armor_type.dex_bonus_max === true) { // Medium = true
          ac += Math.min(2, Math.max(0, character.current_ability_scores.DEX));
        } // Heavy = false
        detail += `${ac}`;
      } else {
        if (base_item.armor_type.dex_bonus_max === null) { // Light = null
          detail += `${ac} + DEX`;
        } else if (base_item.armor_type.dex_bonus_max === true) { // Medium = true
          detail += `${ac} + DEX (Max 2)`;
        } else { // Heavy = false
          detail += `${ac}`;
        }
      }
      return_us.push(
        <Grid key={`${base_item._id}_ac`}>
          <span style={{ fontWeight: "bold" }}>Base Armor Class:&nbsp;</span> 
          { detail }
        </Grid>
      );
    } else if (base_item instanceof Ammunition) {
      return_us.push(
        <Grid key={`${base_item._id}_bundle_size`}>
          <span style={{ fontWeight: "bold" }}>Bundle Size:&nbsp;</span> 
          { base_item.bundle_size }
        </Grid>
      );
    } else if (base_item instanceof Container) {
      return_us.push(
        <Grid key={`${base_item._id}_capacity`}>
          <span style={{ fontWeight: "bold" }}>Capacity:&nbsp;</span> 
          { base_item.max_weight }
        </Grid>
      );
      if (base_item.ignore_weight) {
        return_us.push(
          <Grid key={`${base_item._id}_ignore_weight`}>
            <span style={{ fontWeight: "bold" }}>Weight contained not felt by carrier</span> 
          </Grid>
        );
      }
    }
    return_us.push(
      <Grid key={`${base_item._id}_desc`}>
        <DisplayStringWithReplacements display_string={ base_item.description } />
      </Grid>
    );
    if (base_item.magical) {
      return_us.push(renderFeatureSource(base_item));
    }
    return (
      <Grid container spacing={0} direction="column">
        { return_us }
      </Grid>
    );
  }

  const renderCharacterWeapon = (char_weapon: CharacterWeapon, rarity_num: number, show_cost: boolean) => {
    const return_us: any[] = [];
    const base_item = char_weapon.base_item;
    if (base_item) {
      if (base_item.magical) {
        // const magic_item = base_item as IMagicItem;
        const attunement: any = null;
        // if (magic_item.attunement) {
        //   if (magic_item.limitations.length === 0) {
        //     attunement = <span>(requires attunement)</span>;
        //   } else {
        //     attunement = ( 
        //       <span>
        //         (requires attunement by a&nbsp;
        //           { magic_item.limitations.map((limit, key) => {
        //             return (
        //               <span key={key}>{ key > 0 && ", " }<DisplayObjects type={limit.type} ids={limit.ids} /></span>
        //             );
        //           })}
        //         )
        //       </span>
        //     );
        //   }
        // }
        const base_item_name = "";
        // if (base_item.magical) {
        //   const magic_item = base_item as IMagicItem;
        //   if (magic_item.base_item) {
        //     base_item_name = ` (${magic_item.base_item.name})`;
        //   }
        // }
        return_us.push(
          <Grid key={`${base_item._id}_type`}>
            { ["Other"].includes(base_item.item_type) ? "Wondrous Item" : base_item.item_type }{ base_item_name },&nbsp;{ RARITY_NUM_TO_STR_MAP[char_weapon.rarity] }&nbsp;{ attunement }
          </Grid>
        );
      } else {
        return_us.push(
          <Grid key={`${base_item._id}_type`}>
            { base_item.item_type }, { RARITY_NUM_TO_STR_MAP[char_weapon.rarity] }
          </Grid>
        );
      }
      
      if (base_item.weight > 0) {
        return_us.push(
          <Grid key={`${base_item._id}_weight`}>
            <span style={{ fontWeight: "bold" }}>Weight:&nbsp;</span> 
            { base_item.weight } lb.
          </Grid>
        );
      }
      if (show_cost && base_item.cost > 0) {
        return_us.push(
          <Grid key={`${base_item._id}_cost`}>
            <span style={{ fontWeight: "bold" }}>Cost:&nbsp;</span> 
            { base_item.cost } gp
          </Grid>
        );
      }
      if (true) {
        let weapon_keyword_names: string[] = [];
        const weapon_keyword_ids = char_util.getWeaponKeywordIds(undefined, char_weapon);
        weapon_keyword_names = WEAPON_KEYWORDS.filter(o => 
          weapon_keyword_ids.includes(o._id) &&
          o.display_in_equipment
        ).map(o => o.name);
        // console.log(weapon_keyword_names);
        if (weapon_keyword_names.includes("Range") || weapon_keyword_names.includes("Ranged")) {
          return_us.push(
            <Grid key={`${base_item._id}_attack_type`}>
              <span style={{ fontWeight: "bold" }}>Attack Type:&nbsp;</span>
              Ranged
            </Grid>
          );
          return_us.push(
            <Grid key={`${base_item._id}_attack_range`}>
              <span style={{ fontWeight: "bold" }}>Range:&nbsp;</span> 
              { char_weapon.range } ft./{ char_weapon.range2 } ft.
            </Grid>
          );
          // const ranged_attacks = char_weapon.attacks.Ranged?.rolls??[]; // as RollPlus[];
          // if (ranged_attacks && ranged_attacks.length > 0) {
          //   return_us.push(
          //     <Grid item key={`${base_item._id}_ranged_damage`}>
          //       <span style={{ fontWeight: "bold" }}>Ranged Damage:&nbsp;</span> 
          //       { ranged_attacks.map((attack, key) => {
          //         return (<span key={key}>{ key > 0 && ", " }{ attack.as_string } { attack.type !== "Typeless" && attack.type }</span>);
          //       })}
          //     </Grid>
          //   );
          // }
        } else {
          return_us.push(
            <Grid key={`${base_item._id}_attack_type`}>
              <span style={{ fontWeight: "bold" }}>Attack Type:&nbsp;</span> 
              Melee
            </Grid>
          );
          return_us.push(
            <Grid key={`${base_item._id}_attack_reach`}>
              <span style={{ fontWeight: "bold" }}>Reach:&nbsp;</span> 
              { char_weapon.reach } ft.
            </Grid>
          );
          // const melee_attacks = base_item.attacks.Melee?.rolls??[]; // as RollPlus[];
          // if (melee_attacks && melee_attacks.length > 0) {
          //   return_us.push(
          //     <Grid item key={`${base_item._id}_melee_damage`}>
          //       <span style={{ fontWeight: "bold" }}>Melee Damage:&nbsp;</span> 
          //       { melee_attacks.map((attack, key) => {
          //         return (<span key={key}>{ key > 0 && ", " }{ attack.as_string } { attack.type !== "Typeless" && attack.type }</span>);
          //       })}
          //     </Grid>
          //   );
          // }
          // if (weapon_keyword_names.includes("Versatile")) {
          //   const versatile_attacks = base_item.attacks.Versatile?.rolls??[]; // as RollPlus[];
          //   if (versatile_attacks && versatile_attacks.length > 0) {
          //     return_us.push(
          //       <Grid item key={`${base_item._id}_versatile_damage`}>
          //         <span style={{ fontWeight: "bold" }}>Versatile Damage:&nbsp;</span> 
          //         { versatile_attacks.map((attack, key) => {
          //           return (<span key={key}>{ key > 0 && ", " }{ attack.as_string } { attack.type !== "Typeless" && attack.type }</span>);
          //         })}
          //       </Grid>
          //     );
          //   }
          // }
          if (weapon_keyword_names.includes("Thrown")) {
            return_us.push(
              <Grid key={`${base_item._id}_attack_range`}>
                <span style={{ fontWeight: "bold" }}>Range:&nbsp;</span> 
                { char_weapon.range } ft./{ char_weapon.range2 } ft.
              </Grid>
            );
            // const thrown_attacks = base_item.attacks.Thrown?.rolls??[]; // as RollPlus[];
            // if (thrown_attacks && thrown_attacks.length > 0) {
            //   return_us.push(
            //     <Grid item key={`${base_item._id}_thrown_damage`}>
            //       <span style={{ fontWeight: "bold" }}>Thrown Damage:&nbsp;</span> 
            //       { thrown_attacks.map((attack, key) => {
            //         return (<span key={key}>{ key > 0 && ", " }{ attack.as_string } { attack.type !== "Typeless" && attack.type }</span>);
            //       })}
            //     </Grid>
            //   );
            // }
          }
        }
        return_us.push(
          <Grid key={`${base_item._id}_properties`}>
            <span style={{ fontWeight: "bold" }}>Properties:&nbsp;</span> 
            { weapon_keyword_names.join(", ") }
          </Grid>
        );
      }
      return_us.push(
        <Grid key={`${base_item._id}_desc`}>
          <DisplayStringWithReplacements display_string={ base_item.description } />
        </Grid>
      );
      if (base_item.magical) {
        return_us.push(renderFeatureSource(base_item));
      }
    }
    return (
      <Grid container spacing={0} direction="column">
        { return_us }
      </Grid>
    );
  }
  
  const renderFeatureSource = (feature_source: FeatureSource) => {
    return (
      <Grid key={feature_source._id} container spacing={0} direction="column">
        { feature_source.feature_bases.filter(o => o.display).map((fb, key) => {
          return fb.features.map((feature, key2) => {
            return (
              <Grid key={`${key}_${key2}`} container spacing={0} direction="column">
                <Grid style={{ fontWeight: "bold" }}>
                  { feature.name }
                </Grid>
                <Grid>
                  <DisplayStringWithReplacements display_string={ feature.description } />
                </Grid>
              </Grid>
            );
          });
        })}
      </Grid>
    );
  }
  
  const renderCharacterFeatureSource = (cfs: CharacterFeatureSource) => {
    return (
      <Grid key={cfs.true_id} container spacing={0} direction="column">
        { cfs.char_feature_bases.filter(o => o.display).map((cfb, key) => {
          return cfb.features.map((cf, key2) => {
            return (
              <Grid key={`${key}_${key2}`} container spacing={0} direction="column">
                <Grid style={{ fontWeight: "bold" }}>
                  { char_util.getStringWithReplacements(cf.name, cfs) }
                </Grid>
                <Grid>
                  <DisplayStringWithReplacements display_string={ char_util.getStringWithReplacements(cf.description, cfs) } />
                </Grid>
              </Grid>
            );
          });
        })}
      </Grid>
    );
  }

  if (item instanceof CharacterItem) {
    return renderCharItem(item);
  } else if (item instanceof PseudoItem) {
    return renderPseudoItem(item);
  } else {
    return renderBaseItem(item, 0, true);
  }
}
